/**
 * Google Gemini Service
 * Uses Gemini 2.5 Pro for website analysis with URL Context (CORS-free)
 */

import { GoogleGenAI } from "@google/genai";
import { getAILanguage, getLanguageConfig, type SupportedLanguage } from '../utils/aiLanguageHelper';
import { AgentRegistry } from '../ai/core/AgentRegistry';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || '';

let genAI: GoogleGenAI | null = null;

/** True when the browser build has a Gemini API key (Marketing Fabrik, Brand Book, client-side flows). */
export function isGeminiConfigured(): boolean {
  return !!GEMINI_API_KEY;
}

// Initialize Gemini client
function getClient(): GoogleGenAI {
  if (!genAI && GEMINI_API_KEY) {
    genAI = new GoogleGenAI({ apiKey: GEMINI_API_KEY });
  }
  if (!genAI) {
    throw new Error('Gemini API key not configured');
  }
  return genAI;
}

// Export for use by impressumParser
export { getClient };

export const geminiService = {
  /**
   * Analyze REAL scraped content from Firecrawl
   * No hallucinations - analyzes actual website content
   */
  async analyzeScrapedContent(
    markdown: string,
    images: string[],
    metadata: any,
    url: string
  ): Promise<any> {
    try {
      const client = getClient();
      // Use Gemini 3.0 Flash for general analysis (fast and cost-effective)
      const modelName = "gemini-3-flash-preview"; 
      const domain = new URL(url).hostname.replace('www.', '');
      
      console.log('🤖 Gemini analyzing REAL scraped content...');
      
      const response = await client.models.generateContent({
        model: modelName,
        contents: [{
          parts: [{
            text: `Analyze this REAL website content for Google Ads campaign creation (scraped by Firecrawl - no guessing!):

URL: ${url}
Domain: ${domain}

ACTUAL WEBSITE CONTENT (Markdown from Firecrawl):
${markdown.substring(0, 50000)}

ACTUAL IMAGES FOUND ON WEBSITE:
${images.slice(0, 20).join('\n')}
Total images: ${images.length}

METADATA:
${JSON.stringify(metadata, null, 2)}

═══════════════════════════════════════════════════════════════
DETAILED ANALYSIS REQUIRED - Extract ALL business context!
═══════════════════════════════════════════════════════════════

1️⃣ BUSINESS MODEL DETECTION:
- B2B: "Unternehmen", "Firmen", "Geschäftskunden", "Enterprise", "Solutions"
- B2C: "Privatkunden", "Verbraucher", "Shop", "Bestellen", pricing for individuals
- B2B2C: Both business AND consumer focus
- Look at pricing, contact forms, language used

2️⃣ USP (Unique Selling Proposition):
- What makes this business UNIQUE?
- Look for: "Warum wir", "Vorteile", "Unterschied", "Anders als"
- Extract the CORE differentiator in ONE sentence

3️⃣ OFFERINGS (Products/Services):
- List ALL specific products or services mentioned
- Be specific: not "IT Services" but "Cloud Migration, IT Support, Security Audits"
- Extract from navigation, headings, service pages

4️⃣ BRAND TONE & ADDRESS FORM:
- FORMAL: "Sie", "Ihr Unternehmen", professional language
- INFORMAL: "Du", "Dein", casual language
- Look at: headlines, CTAs, body text
- Quote an EXAMPLE sentence as evidence

5️⃣ INDUSTRY DETECTION:
- AUTOMOTIVE: "fahrzeug", "auto", "kfz", car brands
- E-COMMERCE: shopping cart, products, checkout
- SAAS: software, trial, demo, subscription
- AI/CONSULTING: AI, consulting, beratung
- Be SPECIFIC!

6️⃣ COMPETITOR ANALYSIS & DIFFERENTIATION:
- Look for mentioned competitors or "vs", "compared to", "unlike others"
- Identify market position: "Marktführer", "Spezialist", "Pionier"
- Extract DIFFERENTIATORS: What makes them DIFFERENT from competition?
- Find UNIQUE AD ANGLES: Fresh perspectives for advertising
- Identify PHRASES TO AVOID: Generic statements competitors also use
- Look for COMPETITIVE ADVANTAGES: Speed, price, quality, service, expertise

⚠️ KRITISCHE FORMATIERUNGSREGELN:
- ALLE Texte in NATÜRLICHER SPRACHE mit Leerzeichen!
- RICHTIG: "AI Solutions & Consulting", "Echomotion GmbH"
- FALSCH: "ai_solutions_and_consulting", "echomotion-gmbh"
- KEINE Unterstriche (_), KEINE technischen Slugs!
- KEINE Bindestriche als Wort-Trenner!

🌐 SPRACHE: ${typeof metadata.language === 'string' ? metadata.language : 'de'}
${metadata.language === 'de' || !metadata.language || typeof metadata.language !== 'string' ? `
- Alle Keywords auf DEUTSCH!
- Beispiele: "KI Beratung", "Unternehmensberatung", "Digitalisierung"
` : `- All keywords in ${String(metadata.language).toUpperCase()}!`}

Return JSON:
{
  "industry": "Industry Name (NATURAL LANGUAGE, e.g., 'AI Solutions & Consulting')",
  "industryConfidence": 85,
  "industryReasoning": "Why this industry - quote evidence from content",
  "alternativeIndustries": [{"name": "Alternative Industry", "confidence": 60}],
  
  "businessName": "Company name from content (clean, no slugs)",
  "title": "Page title",
  "valueProposition": "Main value proposition in 1-2 sentences",
  "targetAudience": "Who is this for?",
  
  "businessContext": {
    "businessModel": "b2b|b2c|b2b2c|unknown",
    "businessModelEvidence": "Quote showing B2B/B2C indicators",
    "usp": "The ONE unique selling proposition",
    "slogan": "Company slogan if found (or null)",
    "offerings": ["Specific Product 1", "Service 2", "Solution 3"],
    "competitors": ["Competitor if mentioned"]
  },
  
  "brandIdentity": {
    "tone": "formal|informal",
    "toneEvidence": "Example sentence showing tone",
    "addressForm": "Sie|Du|mixed|none"
  },
  
  "competitorContext": {
    "mentionedCompetitors": ["Competitor 1 if found"],
    "marketPosition": "How they position themselves (e.g., 'Marktführer', 'Spezialist')",
    "differentiators": ["What makes them unique vs competition"],
    "avoidPhrases": ["Generic phrases competitors also use - avoid these!"],
    "uniqueAdAngles": ["Fresh, unique perspectives for ad copy"],
    "competitiveAdvantages": ["Speed", "Quality", "Price", "Expertise"]
  },
  
  "keyBenefits": ["Benefit 1", "Benefit 2", "Benefit 3"],
  "callToAction": "Main CTA text",
  "detectedLanguage": "${typeof metadata.language === 'string' ? metadata.language : 'de'}",
  "detectedLanguageName": "${metadata.language === 'de' || !metadata.language ? 'German' : metadata.language === 'en' ? 'English' : 'Unknown'}",
  "suggestedKeywords": ["keyword1", "keyword2", "keyword3", "keyword4", "keyword5"],
  "detectedConversions": [],
  "recommendedBudget": {"min": 20, "max": 200, "recommended": 50}
}`
          }]
        }],
        config: {
          temperature: 0.1,
          maxOutputTokens: 8000,
          responseMimeType: "application/json"
        }
      });
      
      const responseText = response.text;
      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      
      const analysis = JSON.parse(cleanedText);
      
      // Helper function to clean any text from slugs/underscores
      const cleanText = (text: string | undefined | null): string => {
        if (!text || typeof text !== 'string') return '';
        return text
          .replace(/_/g, ' ')              // Replace underscores with spaces
          .replace(/([a-z])-([a-z])/gi, '$1 $2')  // Replace word-hyphens with spaces
          .replace(/\s+/g, ' ')            // Remove double spaces
          .trim()
          .split(' ')
          .map(word => {
            // Title case for lowercase words, preserve acronyms
            if (word === word.toLowerCase() && word.length > 2) {
              return word.charAt(0).toUpperCase() + word.slice(1);
            }
            return word;
          })
          .join(' ');
      };
      
      // Clean all text fields
      analysis.industry = cleanText(analysis.industry);
      analysis.businessName = cleanText(analysis.businessName);
      analysis.valueProposition = cleanText(analysis.valueProposition);
      
      // Clean businessContext
      if (analysis.businessContext) {
        analysis.businessContext.usp = cleanText(analysis.businessContext.usp);
        analysis.businessContext.slogan = cleanText(analysis.businessContext.slogan);
        if (analysis.businessContext.offerings) {
          analysis.businessContext.offerings = analysis.businessContext.offerings.map(cleanText);
        }
      }
      
      // Clean brandIdentity
      if (analysis.brandIdentity) {
        analysis.brandIdentity.toneEvidence = cleanText(analysis.brandIdentity.toneEvidence);
      }
      
      // Clean keywords
      if (analysis.suggestedKeywords) {
        analysis.suggestedKeywords = analysis.suggestedKeywords.map(cleanText);
      }
      
      // Clean keyBenefits
      if (analysis.keyBenefits) {
        analysis.keyBenefits = analysis.keyBenefits.map(cleanText);
      }
      
      // Clean alternativeIndustries
      if (analysis.alternativeIndustries) {
        analysis.alternativeIndustries = analysis.alternativeIndustries.map((alt: any) => ({
          ...alt,
          name: cleanText(alt.name)
        }));
      }
      
      // Clean competitorContext
      if (analysis.competitorContext) {
        if (analysis.competitorContext.mentionedCompetitors) {
          analysis.competitorContext.mentionedCompetitors = analysis.competitorContext.mentionedCompetitors.map(cleanText);
        }
        analysis.competitorContext.marketPosition = cleanText(analysis.competitorContext.marketPosition);
        if (analysis.competitorContext.differentiators) {
          analysis.competitorContext.differentiators = analysis.competitorContext.differentiators.map(cleanText);
        }
        if (analysis.competitorContext.avoidPhrases) {
          analysis.competitorContext.avoidPhrases = analysis.competitorContext.avoidPhrases.map(cleanText);
        }
        if (analysis.competitorContext.uniqueAdAngles) {
          analysis.competitorContext.uniqueAdAngles = analysis.competitorContext.uniqueAdAngles.map(cleanText);
        }
        if (analysis.competitorContext.competitiveAdvantages) {
          analysis.competitorContext.competitiveAdvantages = analysis.competitorContext.competitiveAdvantages.map(cleanText);
        }
      }
      
      console.log(`✅ Gemini analyzed REAL content: Industry = ${analysis.industry}`);
      console.log(`   Business Model: ${analysis.businessContext?.businessModel || 'unknown'}`);
      console.log(`   USP: ${analysis.businessContext?.usp || 'not detected'}`);
      console.log(`   Tone: ${analysis.brandIdentity?.tone || 'unknown'} (${analysis.brandIdentity?.addressForm || 'unknown'})`);
      
      return analysis;
      
    } catch (error: any) {
      console.error('Gemini analysis error:', error);
      throw error;
    }
  },

  /**
   * Analyze product/service images with Gemini Vision
   * Extracts visual context for better ad copy generation
   */
  async analyzeProductImages(
    imageUrls: string[],
    businessContext?: { industry?: string; businessName?: string }
  ): Promise<{
    analyzedImages: number;
    detectedProducts: string[];
    detectedServices: string[];
    visualStyle: 'professional' | 'casual' | 'luxury' | 'technical' | 'friendly';
    dominantColors: string[];
    atmosphere: string;
    suggestedImageDescriptions: string[];
  }> {
    try {
      const client = getClient();
      
      // Take top 5 images for analysis (balance between context and API costs)
      const imagesToAnalyze = imageUrls.slice(0, 5);
      
      if (imagesToAnalyze.length === 0) {
        console.log('⚠️ No images to analyze');
        return {
          analyzedImages: 0,
          detectedProducts: [],
          detectedServices: [],
          visualStyle: 'professional',
          dominantColors: [],
          atmosphere: 'Nicht erkannt',
          suggestedImageDescriptions: []
        };
      }
      
      console.log(`🖼️ Analyzing ${imagesToAnalyze.length} images with Gemini Vision...`);
      
      const response = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Use Flash 3.0 for multimodal analysis
        contents: [{
          parts: [
            {
              text: `Analyze these ${imagesToAnalyze.length} images from a business website.
${businessContext?.industry ? `Business Context: ${businessContext.industry}` : ''}
${businessContext?.businessName ? `Company: ${businessContext.businessName}` : ''}

IMAGE URLs to analyze:
${imagesToAnalyze.map((url, i) => `${i + 1}. ${url}`).join('\n')}

Analyze ALL images and extract:

1. PRODUCTS: What physical products are shown? (Be specific: "Red sports car", "Laptop computer", etc.)
2. SERVICES: What services are depicted? (e.g., "Consulting meeting", "Car repair", "Software demo")
3. VISUAL STYLE: Overall aesthetic (professional/casual/luxury/technical/friendly)
4. COLORS: Dominant brand colors visible (as hex codes if possible)
5. ATMOSPHERE: Emotional feeling (e.g., "Trustworthy and modern", "Exciting and dynamic")
6. AD DESCRIPTIONS: 3-5 short descriptions suitable for Google Ads image assets

Return JSON:
{
  "detectedProducts": ["Product 1", "Product 2"],
  "detectedServices": ["Service 1", "Service 2"],
  "visualStyle": "professional|casual|luxury|technical|friendly",
  "dominantColors": ["#XXXXXX", "#YYYYYY"],
  "atmosphere": "Brief description of overall mood",
  "suggestedImageDescriptions": [
    "Short ad-ready description 1 (max 25 chars)",
    "Short ad-ready description 2 (max 25 chars)"
  ]
}`
            }
          ]
        }],
        config: {
          temperature: 0.2,
          maxOutputTokens: 2000,
          responseMimeType: "application/json"
        }
      });
      
      const responseText = response.text;
      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      
      const imageAnalysis = JSON.parse(cleanedText);
      
      console.log(`✅ Vision AI analyzed ${imagesToAnalyze.length} images`);
      console.log(`   Products: ${imageAnalysis.detectedProducts?.length || 0}`);
      console.log(`   Services: ${imageAnalysis.detectedServices?.length || 0}`);
      console.log(`   Style: ${imageAnalysis.visualStyle}`);
      
      return {
        analyzedImages: imagesToAnalyze.length,
        detectedProducts: imageAnalysis.detectedProducts || [],
        detectedServices: imageAnalysis.detectedServices || [],
        visualStyle: imageAnalysis.visualStyle || 'professional',
        dominantColors: imageAnalysis.dominantColors || [],
        atmosphere: imageAnalysis.atmosphere || 'Nicht erkannt',
        suggestedImageDescriptions: imageAnalysis.suggestedImageDescriptions || []
      };
      
    } catch (error: any) {
      console.error('Vision analysis error:', error);
      // Return empty result on error, don't block the flow
      return {
        analyzedImages: 0,
        detectedProducts: [],
        detectedServices: [],
        visualStyle: 'professional',
        dominantColors: [],
        atmosphere: 'Analyse fehlgeschlagen',
        suggestedImageDescriptions: []
      };
    }
  },

  /**
   * Legacy method - now deprecated, use analyzeScrapedContent instead
   */
  async analyzeWebsite(url: string): Promise<any> {
    try {
      const client = getClient();
      
      const domain = new URL(url).hostname.replace('www.', '');
      const businessName = domain.split('.')[0];
      
      console.log('🔍 Gemini Analyzing website via Google Search...');
      
      const response = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Use Flash 3.0 for search-grounded tasks
        contents: [{
          parts: [{
              text: `Use Google Search to comprehensively analyze the website ${url} for creating a Google Ads campaign.

SEARCH STRATEGY:
1. Search: "${domain}" - Get general business information
2. Search: "${businessName} was ist das" - Understand what the business does
3. Search: "${domain} Unternehmen Branche" - Get industry classification
4. Search: "${domain} Dienstleistungen Produkte" - Understand offerings
5. If German domain (.de/.at/.ch), search in German

Based on all search results, analyze:

INDUSTRY DETECTION - BE VERY SPECIFIC AND ACCURATE:

Analyze the URL, content, images, and overall context to determine the EXACT industry:

**AUTOMOTIVE/CAR DEALER indicators:**
- German: "fahrzeug", "auto", "kfz", "gebrauchtwagen", "neuwagen", "autohaus", "probefahrt", "werkstatt", car brand names (Audi, VW, BMW, Mercedes, etc.)
- English: "vehicle", "car dealer", "test drive", "showroom", "automotive"
- Domain patterns: autohaus, mahag, kfz, vehicle, car-dealer
- Visual: Car images, vehicle listings, showroom photos

**E-COMMERCE indicators:**
- Shopping cart, product grid, "in den warenkorb", checkout, payment
- Product categories, pricing, "jetzt kaufen"

**SAAS/SOFTWARE indicators:**
- "Free trial", "Demo", "Subscription", software features, pricing tiers
- "Kostenlos testen", "Demo anfordern"

**REAL ESTATE indicators:**
- "Immobilien", properties, apartments, "wohnung", "haus", "mieten", "kaufen"
- Property listings, square meters, location maps

**IMPORTANT:** 
- If you see car-related content → Use "Automotive" or "Car Dealer", NOT "Professional Services"
- If you see product listings → Use "E-commerce", NOT "Retail"
- Be as SPECIFIC as possible based on actual content

BUSINESS INFO:
- Look for "Impressum" page for company details
- Extract: Business name, city, country, contact info

CONVERSIONS:
- Find actual forms, contact buttons, booking systems
- Look for: Contact forms, Appointment booking, Quote requests, Downloads, Purchases
- German pages often have: "Kontakt", "Termin vereinbaren", "Angebot anfordern", "Probefahrt buchen"

LANGUAGE:
- Detect actual content language from text
- Don't just use domain TLD

IMAGES:
- Extract marketing-suitable image URLs (not icons/logos)
- Look for high-quality photos (products, services, people, locations)

Return detailed JSON with this exact structure:
{
  "industry": "Automotive|Car Dealer|E-commerce|SaaS|Real Estate|Healthcare|Finance|Professional Services|etc.",
  "industryConfidence": 95,
  "industryEvidence": "Detailed evidence: found car listings, Audi/VW branding, Probefahrt forms, vehicle images",
  "title": "Website main title",
  "businessName": "Company name (from content or impressum)",
  "valueProposition": "Main value proposition",
  "targetAudience": "Target audience description",
  "keyBenefits": ["benefit 1", "benefit 2", "benefit 3"],
  "features": ["feature 1", "feature 2"],
  "callToAction": "Primary call to action text",
  "detectedLanguage": "de",
  "detectedLanguageName": "German",
  "suggestedKeywords": ["keyword1", "keyword2", "keyword3", "keyword4", "keyword5"],
  "tone": "professional|casual|urgent|friendly",
  "recommendedCampaignType": "search|pmax",
  "recommendedBudget": {"min": 20, "max": 200, "recommended": 50},
  "detectedConversions": [
    {
      "type": "lead|contact|purchase|signup|download",
      "name": "Descriptive conversion name",
      "value": 500,
      "confidence": 85,
      "pageUrl": "/probefahrt or /contact",
      "fullUrl": "https://example.com/probefahrt",
      "selector": ".contact-form or description",
      "evidence": "Detailed evidence of why this is a conversion",
      "description": "What the user does to convert"
    }
  ],
  "suggestedConversionValue": 500,
  "hasEcommerce": false,
  "hasForms": true,
  "imageUrls": ["https://example.com/image1.jpg", "https://example.com/image2.jpg"],
  "logoUrl": "https://example.com/logo.png",
  "businessInfo": {
    "businessName": "Company Name",
    "street": "Street address if found",
    "city": "City",
    "postalCode": "Postal code",
    "country": "Germany",
    "phone": "Phone if found",
    "email": "Email if found"
  },
  "trackingTags": {
    "hasGoogleAnalytics": false,
    "hasGoogleTagManager": false,
    "hasGoogleAds": false,
    "detectedTags": []
  }
}`
            }
          ]
        }],
        tools: [{
          googleSearch: {}  // Use Google Search for additional context
        }],
        config: {
          temperature: 0.1,  // Low temperature for factual extraction
          maxOutputTokens: 8000,
          responseMimeType: "application/json"
        }
      });
      
      // Unified SDK: response.text is a property
      const responseText = response.text || '';
      
      if (!responseText) {
        console.error('Gemini response structure:', JSON.stringify(response, null, 2));
        throw new Error('Empty response from Gemini');
      }
      
      // Clean response text (remove markdown code blocks if present)
      const cleanedText = responseText
        .replace(/^```json\s*/gi, '')
        .replace(/^```\s*/gi, '')
        .replace(/```\s*$/gi, '')
        .trim();
      
      const analysis = JSON.parse(cleanedText);
      
      console.log(`✅ Gemini analysis complete: Industry = ${analysis.industry} (${analysis.industryConfidence || 0}% confidence)`);
      console.log(`📍 Business: ${analysis.businessName || 'Unknown'} in ${analysis.businessInfo?.city || 'Unknown'}`);
      console.log(`🌐 Language: ${analysis.detectedLanguageName || 'Unknown'}`);
      console.log(`🎯 Conversions found: ${analysis.detectedConversions?.length || 0}`);
      
      return analysis;
      
    } catch (error: any) {
      console.error('❌ Gemini analysis error:', error);
      throw new Error(`Gemini API error: ${error.message}`);
    }
  },

  async extractImages(url: string): Promise<string[]> {
    try {
      const client = getClient();
      const domain = new URL(url).hostname;
      
      const result = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Use Flash 3.0 for image extraction/search
        contents: [{
          role: "user",
          parts: [{
            text: `Search Google for images from ${domain}.
            
            Find ALL image URLs from this website that are suitable for a brand stylebook.
            Include:
            - Product photos
            - Team / People photos
            - Office / Location photos
            - Hero banner images
            - Brand atmosphere images
            
            Exclude:
            - Tiny icons (< 50px)
            - Social media logos (Facebook, LinkedIn icons)
            - Generic UI elements (arrows, hamburger menus)
            
            Return a JSON array of image URLs. Try to find at least 10-15 high-quality images.`
          }]
        }],
        tools: [{ googleSearch: {} }],
        config: {
          responseMimeType: "application/json",
          maxOutputTokens: 4000
        }
      });
      
      // Unified SDK: response.text is a property
      const responseText = result.text || '[]';
      
      // Clean markdown wrappers
      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      const imageUrls = JSON.parse(cleanedText || '[]');
      console.log(`✅ Gemini found ${imageUrls?.length || 0} marketing images via search`);
      
      return Array.isArray(imageUrls) ? imageUrls : [];
      
    } catch (error) {
      console.error('Error extracting images with Gemini:', error);
      return [];
    }
  },

  /**
   * Enrich industry detection with Google Search
   */
  async enrichIndustryWithSearch(domain: string): Promise<{
    industry: string;
    confidence: number;
    evidence: string;
  }> {
    try {
      const client = getClient();
      
      const result = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Use Flash 3.0 for enrichment
        contents: [{
          role: "user",
          parts: [{
            text: `Search Google for: "${domain} was ist das für ein Unternehmen"
            
            Based on search results, determine:
            1. Exact industry/business type
            2. Confidence level (0-100)
            3. Evidence from search results
            
            Return JSON: {"industry": "...", "confidence": 95, "evidence": "..."}`
          }]
        }],
        tools: [{
          googleSearch: {}
        }],
        config: {
          responseMimeType: "application/json",
          maxOutputTokens: 500
        }
      });
      
      // Unified SDK: response.text is a property, not a method
      const responseText = result.text || result.response?.text || '';
      return JSON.parse(responseText);
      
    } catch (error) {
      console.error('Error enriching industry:', error);
      return {
        industry: 'Unknown',
        confidence: 0,
        evidence: 'Search failed'
      };
    }
  },

  /**
   * Extract colors directly from a logo/image URL using Gemini Vision
   */
  async extractColorsFromImage(imageUrl: string): Promise<string[]> {
    try {
      const client = getClient();
      
      const response = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Use Flash 3.0 for vision
        contents: [{
          role: "user",
          parts: [{
            inlineData: {
              mimeType: "image/jpeg",
              data: await this.fetchImageAsBase64(imageUrl)
            }
          }, {
            text: `Analyze this logo image and extract the EXACT hex color codes used.
            Return a JSON array of hex codes, sorted by visual prominence/area.
            Example: ["#E63946", "#1F2937", "#F1FAEE"]
            Exclude white and black unless they are truly part of the brand identity.`
          }]
        }],
        config: {
          responseMimeType: "application/json",
          maxOutputTokens: 300
        }
      });
      
      // Unified SDK: response.text is a property
      const responseText = response.text || '';
      
      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      const colors = JSON.parse(cleanedText || '[]');
      
      return Array.isArray(colors) ? colors.slice(0, 5) : [];
    } catch (error) {
      console.error('Error extracting colors from image:', error);
      return [];
    }
  },

  /**
   * Helper: Fetch image and convert to base64
   */
  async fetchImageAsBase64(imageUrl: string): Promise<string> {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Error fetching image:', error);
      throw error;
    }
  },

  async extractBrandColors(url: string, html?: string): Promise<string[]> {
    try {
      // If we have HTML, extract colors from CSS/inline styles first
      if (html) {
        const colorRegex = /#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})\b/g;
        const matches = html.match(colorRegex) || [];
        const uniqueColors = Array.from(new Set(matches));
        
        // Filter out common generic colors
        const filtered = uniqueColors.filter(color => {
          const c = color.toLowerCase();
          // Exclude pure black, pure white, very light grays
          if (c === '#000000' || c === '#ffffff' || c === '#fff' || c === '#000') return false;
          if (c.match(/#f{6}|#0{6}|#[ef]{6}/)) return false; // Very light or very dark
          // Exclude standard bootstrap/tailwind blues
          if (c === '#007bff' || c === '#3b82f6' || c === '#0d6efd') return false;
          return true;
        });
        
        if (filtered.length >= 3) {
          console.log(`✅ Extracted ${filtered.length} brand colors from HTML/CSS`);
          return filtered.slice(0, 10); // Return top 10
        }
      }
      
      // Fallback: Use Gemini with Google Search (less reliable but better than nothing)
      const client = getClient();
      
      const response = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Use Flash 3.0
        contents: [{
          role: "user",
          parts: [{
            text: `Analyze the website ${url} to find brand colors.
            
            Return a JSON array of hex color codes that represent the brand identity.
            Focus on: primary action colors, logo colors, header/nav colors.
            Exclude: pure black/white, generic grays, default link blues.
            
            Return format: ["#HEX1", "#HEX2", "#HEX3"]
            Maximum 5 colors.`
          }]
        }],
        tools: [{ googleSearch: {} }],
        config: {
          responseMimeType: "application/json",
          maxOutputTokens: 500
        }
      });

      // Unified SDK: response.text is a property
      const responseText = response.text || '';

      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      const colors = JSON.parse(cleanedText || '[]');
      
      console.log(`✅ Gemini extracted brand colors via search: ${colors.join(', ')}`);
      return Array.isArray(colors) ? colors : [];
    } catch (error) {
      console.error('Error extracting brand colors:', error);
      return [];
    }
  },

  /**
   * Detect industry for customer onboarding (3 specific industries)
   * Used by onboarding wizard to determine template and modules
   */
  async detectIndustryForOnboarding(context: {
    markdown: string;
    businessName: string;
    metaDescription: string;
    images: string[];
  }): Promise<{
    industry: 'automotive' | 'insurance' | 'saas';
    confidence: number;
    reasoning: string;
    businessModel?: 'b2b' | 'b2c' | 'b2b2c';
    offerings?: string[];
    usp?: string;
    slogan?: string;
    targetAudience?: string;
    brandTone?: 'formal' | 'casual';
    socialMedia?: {
      linkedin?: string;
      instagram?: string;
      facebook?: string;
      twitter?: string;
    };
    competitors?: string[];
    keywords?: string[];
    brandColors?: string[];
    brandFonts?: string[];
    alternatives?: Array<{ name: string; confidence: number }>;
  }> {
    try {
      const client = getClient();
      
      console.log('🤖 Gemini detecting industry for onboarding...');
      
      const prompt = `Analyze this website and determine the business type for CRM onboarding.

Website Content (first 5000 chars):
${context.markdown.substring(0, 5000)}

Business Name: ${context.businessName || 'Unknown'}
Meta Description: ${context.metaDescription || 'Not available'}
Images Found: ${context.images.length} images

ONLY these 7 industries are supported:

1. **consulting** - Professional services, consulting, advisory, B2B expertise, Data & IT
   Keywords: consulting, advisory, strategy, management, professional services, data analytics, IT services, digital transformation, AI, Beratung, Consulting, Strategie, Interim, Workshop, Daten, Analyse, IT-Beratung

2. **saas** - Software as a service, cloud platforms, subscription software, Tech products
   Keywords: pricing plans, free trial, SaaS, software, platform, API, dashboard, subscription, cloud, AI tool, Demo, Testversion, Abo, Softwarelösung

3. **automotive** - Car dealerships, vehicle sales, service centers, parts dealers
   Keywords: test drive, vehicles, cars, dealership, service center, automotive, KFZ, Fahrzeuge, Gebrauchtwagen, Neuwagen, Probefahrt, Werkstatt

4. **insurance** - Insurance agencies, brokers, policy providers
   Keywords: insurance, policy, coverage, premium, claims, broker, Versicherung, Prämie, Policen, Schadenfall, Makler

5. **ecommerce** - Online shops, e-commerce, retail, product sales
   Keywords: shop, store, e-commerce, online shop, products, cart, checkout, Warenkorb, bestellen, kaufen, Online-Shop

6. **agency** - Marketing agencies, advertising, design, creative services
   Keywords: marketing agency, advertising, digital marketing, SEO, social media, creative agency, Werbeagentur, Marketing-Agentur, Kreativagentur, Performance Marketing

7. **finance** - Banks, investment firms, financial advisors, wealth management
   Keywords: bank, banking, investment, financial advisor, wealth management, Finanzberatung, Investmentberatung, Vermögensberatung, Baufinanzierung, Kredit, Girokonto, Sparkonto

BUSINESS MODEL:
- b2c: Selling to consumers (e.g., car buyers, individual insurance customers)
- b2b: Selling to businesses (e.g., fleet sales, business insurance, enterprise software)
- b2b2c: Both models

CRITICAL INSTRUCTIONS:
- You MUST choose one of the 3 industries above
- Use the 'googleSearch' tool to verify facts if content is sparse
- Base your decision ONLY on the actual content provided or verified via search
- If it doesn't clearly fit any category, choose the closest match and set lower confidence
- For German content: "Fahrzeuge" = automotive, "Versicherung" = insurance, "Software" = saas
- Include detected offerings (products/services mentioned) - DO NOT HALLUCINATE, extract from text
- USP and Slogan must be extracted from the real website text, do not invent them.

Return ONLY valid JSON (no markdown, no code blocks):
{
  "industry": "automotive" | "insurance" | "saas" | "consulting" | "ecommerce" | "agency" | "finance",
  "confidence": 0-100,
  "reasoning": "2-3 sentences explaining why based on actual content",
  "businessModel": "b2b" | "b2c" | "b2b2c",
  "offerings": ["Product 1", "Product 2", "Product 3"],
  "usp": "Unique Selling Proposition (one sentence)",
  "slogan": "Catchy slogan matching the brand (in German)",
  "targetAudience": "Ideal Customer Profile description",
  "brandTone": "formal" | "casual",
  "socialMedia": {
    "linkedin": "url or null",
    "instagram": "url or null",
    "facebook": "url or null",
    "twitter": "url or null"
  },
  "competitors": ["Competitor 1", "Competitor 2", "Competitor 3"],
  "keywords": ["Keyword 1", "Keyword 2", "Keyword 3 (10-20 keywords)"],
  "brandColors": ["#hex1", "#hex2", "#hex3"],
  "brandFonts": ["Font Family 1", "Font Family 2"],
  "alternatives": [
    { "name": "second_best_match", "confidence": 30 }
  ]
}`;

      const response = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Use Flash 3.0
        contents: [{
          parts: [{ text: prompt }]
        }],
        tools: [{ googleSearch: {} }], // Use Google Search for grounding
        config: {
          temperature: 0.1, // Very low temperature for factual data
          maxOutputTokens: 2000
        }
      });
      
      // DEBUG LOGGING
      console.log('Gemini Response Object:', response);

      // Unified SDK: response.text is a property
      let cleanResult = response.text || '';
      
      if (!cleanResult) {
        throw new Error('Empty response from Gemini');
      }
      
      console.log('Raw Gemini response:', cleanResult);
      
      // Clean response (remove markdown code blocks if present)
      cleanResult = cleanResult.trim();
      if (cleanResult.startsWith('```')) {
        cleanResult = cleanResult.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      }
      
      const parsed = JSON.parse(cleanResult);
      
      console.log(`✅ Industry detected: ${parsed.industry} (${parsed.confidence}% confidence)`);
      console.log(`   Reasoning: ${parsed.reasoning}`);
      
      return parsed;
      
    } catch (error) {
      console.error('Error detecting industry for onboarding:', error);
      
      // Fallback: Try to detect from simple keywords
      const markdown = context.markdown.toLowerCase();
      
      if (markdown.includes('fahrzeug') || markdown.includes('auto') || markdown.includes('kfz') || 
          markdown.includes('vehicle') || markdown.includes('car') || markdown.includes('dealership')) {
        return {
          industry: 'automotive',
          confidence: 60,
          reasoning: 'Fallback detection based on automotive keywords in content',
          businessModel: 'b2c'
        };
      }
      
      if (markdown.includes('versicherung') || markdown.includes('insurance') || 
          markdown.includes('policy') || markdown.includes('coverage')) {
        return {
          industry: 'insurance',
          confidence: 60,
          reasoning: 'Fallback detection based on insurance keywords in content',
          businessModel: 'b2c'
        };
      }
      
      if (markdown.includes('software') || markdown.includes('saas') || markdown.includes('api') || 
          markdown.includes('platform') || markdown.includes('trial')) {
        return {
          industry: 'saas',
          confidence: 60,
          reasoning: 'Fallback detection based on SaaS keywords in content',
          businessModel: 'b2b'
        };
      }
      
      if (markdown.includes('consulting') || markdown.includes('beratung') || markdown.includes('advisory') || 
          markdown.includes('professional services') || markdown.includes('strategie')) {
        return {
          industry: 'consulting',
          confidence: 60,
          reasoning: 'Fallback detection based on consulting keywords in content',
          businessModel: 'b2b'
        };
      }
      
      if (markdown.includes('shop') || markdown.includes('store') || markdown.includes('e-commerce') || 
          markdown.includes('ecommerce') || markdown.includes('warenkorb') || markdown.includes('bestellen') ||
          markdown.includes('online-shop') || markdown.includes('kaufen')) {
        return {
          industry: 'ecommerce',
          confidence: 60,
          reasoning: 'Fallback detection based on e-commerce keywords in content',
          businessModel: 'b2c'
        };
      }
      
      if (markdown.includes('marketing agency') || markdown.includes('werbeagentur') || markdown.includes('agentur') ||
          markdown.includes('advertising') || markdown.includes('kreativ') || markdown.includes('digital marketing') ||
          markdown.includes('performance marketing') || markdown.includes('social media marketing')) {
        return {
          industry: 'agency',
          confidence: 60,
          reasoning: 'Fallback detection based on marketing agency keywords in content',
          businessModel: 'b2b'
        };
      }
      
      if (markdown.includes('bank') || markdown.includes('banking') || markdown.includes('investment') ||
          markdown.includes('finanzberatung') || markdown.includes('vermögensberatung') || 
          markdown.includes('baufinanzierung') || markdown.includes('kredit') || 
          markdown.includes('girokonto') || markdown.includes('sparkonto')) {
        return {
          industry: 'finance',
          confidence: 60,
          reasoning: 'Fallback detection based on banking/finance keywords in content',
          businessModel: 'b2c'
        };
      }
      
      // Ultimate fallback: Finance with low confidence
      return {
        industry: 'finance',
        confidence: 30,
        reasoning: 'Could not clearly detect industry from content',
        businessModel: 'b2c'
      };
    }
  },

  /**
   * Extract detailed sales strategy information from website
   * For Sales Strategy Page in onboarding
   */
  async extractSalesStrategy(context: {
    markdown: string;
    html: string;
    businessName: string;
    industry: string;
    businessModel?: 'b2b' | 'b2c' | 'b2b2c';
    competitors?: string[];
    language?: SupportedLanguage; // Optional language for output
  }): Promise<{
    businessModel: 'b2b' | 'b2c' | 'b2b2c';
    targetAudienceSegment: 'startup' | 'smb' | 'mid_market' | 'enterprise' | 'consumer' | 'mixed';
    targetAudienceDescription: string;
    products: Array<{
      name: string;
      description: string;
      pricing?: string;
      pricingModel?: 'subscription' | 'one_time' | 'usage_based' | 'quote';
      url?: string;
    }>;
    conversionPoints: Array<{
      type: 'form' | 'button' | 'cta' | 'phone' | 'email' | 'chat';
      label: string;
      url?: string;
    }>;
    idealCustomerProfile: {
      companySize?: string;
      budget?: string;
      industry?: string[];
      painPoints?: string[];
    };
  }> {
    try {
      const client = getClient();
      
      // Get language configuration
      const langConfig = context.language 
        ? getLanguageConfig(context.language) 
        : getAILanguage();
      const isGerman = langConfig.code === 'de';
      
      console.log(`🎯 Extracting sales strategy data (${langConfig.name})...`);
      
      const languageInstruction = isGerman
        ? '\n\n🌐 SPRACHE: Alle Beschreibungen und Texte müssen auf DEUTSCH sein!'
        : '\n\n🌐 LANGUAGE: All descriptions and text must be in ENGLISH!';
      
      const response = await client.models.generateContent({
        model: "gemini-3-flash-preview", // Use Flash 3.0
        contents: [{
          parts: [{
            text: `Analyze this website content and extract DETAILED sales strategy information:
${languageInstruction}

BUSINESS: ${context.businessName}
INDUSTRY: ${context.industry}
BUSINESS MODEL (pre-detected): ${context.businessModel || 'unknown'}

WEBSITE CONTENT (first 10000 chars):
${context.markdown.substring(0, 10000)}

HTML (for conversion detection):
${context.html.substring(0, 5000)}

EXTRACT THE FOLLOWING:

1. BUSINESS MODEL (verify/refine):
   - b2b: Enterprise software, B2B services, fleet sales
   - b2c: Consumer products, individual services
   - b2b2c: Platforms, marketplaces

2. TARGET AUDIENCE SEGMENT:
   - startup: Small teams, limited budget, mentions "startup", "gründer"
   - smb: Small-medium businesses, 10-500 employees
   - mid_market: 500-2000 employees, established companies
   - enterprise: 2000+ employees, "enterprise", "konzern", Fortune 500
   - consumer: Individual end-users (B2C)
   - mixed: Multiple segments

3. PRODUCTS/SERVICES (from ACTUAL content):
   - Extract from pricing pages, product listings, service descriptions
   - Find pricing information (€, $, "ab X/Monat", etc.)
   - Detect pricing model: subscription, one-time, usage-based, quote-based
   - Find product URLs if mentioned

4. CONVERSION POINTS (from ACTUAL HTML/content):
   - Forms: Contact form, demo request, newsletter signup
   - Buttons: "Jetzt kaufen", "Demo buchen", "Kontakt", "Buy now"
   - CTAs: "Get started", "Kostenlos testen", "Angebot anfordern"
   - Phone numbers, Email addresses for contact
   - Chat widgets, booking systems

5. IDEAL CUSTOMER PROFILE:
   - Company size preferences (based on case studies, testimonials)
   - Budget indicators (pricing tier names, "enterprise", "pro")
   - Industries served (if mentioned)
   - Pain points addressed (from value props)

Return ONLY valid JSON:
{
  "businessModel": "b2b" | "b2c" | "b2b2c",
  "targetAudienceSegment": "startup" | "smb" | "mid_market" | "enterprise" | "consumer" | "mixed",
  "targetAudienceDescription": "Detailed description of who they sell to",
  "products": [
    {
      "name": "Product name from actual content",
      "description": "Brief description",
      "pricing": "€49/Monat" or "Ab €1,500" or "Auf Anfrage",
      "pricingModel": "subscription" | "one_time" | "usage_based" | "quote",
      "url": "/pricing or /products/name (if found)"
    }
  ],
  "conversionPoints": [
    {
      "type": "form" | "button" | "cta" | "phone" | "email" | "chat",
      "label": "Actual text from website",
      "url": "/contact or similar (if found)"
    }
  ],
  "idealCustomerProfile": {
    "companySize": "10-500 employees" or "Individual consumers",
    "budget": "€500-€5,000/month" or "€20-€100/item",
    "industry": ["Technology", "Finance"],
    "painPoints": ["Manual processes", "High costs"]
  }
}`
          }]
        }],
        config: {
          temperature: 0.1,
          maxOutputTokens: 4000,
          responseMimeType: "application/json"
        }
      });

      // Unified SDK: response.text is a property
      const responseText = response.text || '';
      const cleanedText = responseText.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      
      const strategy = JSON.parse(cleanedText);
      
      console.log(`✅ Sales strategy extracted: ${strategy.businessModel}, ${strategy.products?.length || 0} products, ${strategy.conversionPoints?.length || 0} conversion points`);
      
      return strategy;
      
    } catch (error) {
      console.error('Error extracting sales strategy:', error);
      // Return safe defaults
      return {
        businessModel: context.businessModel || 'b2c',
        targetAudienceSegment: 'mixed',
        targetAudienceDescription: 'General audience',
        products: [],
        conversionPoints: [],
        idealCustomerProfile: {
          companySize: 'Unknown',
          budget: 'Unknown',
          industry: [],
          painPoints: []
        }
      };
    }
  },

  /**
   * Check if Gemini API is configured and available
   */
  isConfigured(): boolean {
    return !!GEMINI_API_KEY && GEMINI_API_KEY.length > 0;
  },

  /**
   * Alias for isConfigured (for compatibility)
   */
  isAvailable(): boolean {
    return this.isConfigured();
  },

  /**
   * Generate Echobot response for contextual tips
   * Uses Gemini 2.5 Pro for intelligent, context-aware responses
   */
  async generateEchobotResponse(context: {
    page: string;
    userRole: string;
    organizationStats: {
      leadCount: number;
      opportunityCount: number;
      campaignCount: number;
    };
    issue?: string;
    userQuestion?: string;
    language?: SupportedLanguage; // Optional language override
  }): Promise<{
    message: string;
    animation: 'celebrate' | 'question' | 'shake' | 'wave';
    actionButton?: { label: string; action: string };
  }> {
    const client = getClient();
    
    // Get language configuration - use provided language or system default
    const langConfig = context.language 
      ? getLanguageConfig(context.language) 
      : getAILanguage();
    
    // Language-specific prompt parts
    const isGerman = langConfig.code === 'de';
    
    const languageInstruction = isGerman
      ? 'IMMER auf Deutsch antworten - verwende keine englischen Begriffe!'
      : 'ALWAYS respond in English - do not use German terms!';
    
    const taskInstruction = context.userQuestion
      ? (isGerman 
          ? 'Beantworte die Frage hilfreich und kurz (max 100 Zeichen).' 
          : 'Answer the question helpfully and briefly (max 100 characters).')
      : (isGerman 
          ? 'Generiere einen hilfreichen, proaktiven Tipp (max 80 Zeichen).' 
          : 'Generate a helpful, proactive tip (max 80 characters).');
    
    const fallbackMessage = isGerman 
      ? 'Ich bin hier um zu helfen! 🤖' 
      : 'I am here to help! 🤖';

    const prompt = `${isGerman ? 'Du bist' : 'You are'} Echobot 🤖, ${isGerman ? 'ein freundlicher Roboter-Assistent für ein CRM-System' : 'a friendly robot assistant for a CRM system'}.

${isGerman ? 'KONTEXT' : 'CONTEXT'}:
- ${isGerman ? 'Aktuelle Seite' : 'Current page'}: ${context.page}
- ${isGerman ? 'User-Rolle' : 'User role'}: ${context.userRole}
- ${isGerman ? 'Organisation hat' : 'Organization has'} ${context.organizationStats.leadCount} Leads, ${context.organizationStats.campaignCount} ${isGerman ? 'Kampagnen' : 'campaigns'}, ${context.organizationStats.opportunityCount} Opportunities
${context.issue ? `- ${isGerman ? 'Problem erkannt' : 'Issue detected'}: ${context.issue}` : ''}
${context.userQuestion ? `- ${isGerman ? 'User fragt' : 'User asks'}: "${context.userQuestion}"` : ''}

${isGerman ? 'AUFGABE' : 'TASK'}:
${taskInstruction}

${isGerman ? 'STIL' : 'STYLE'}:
- ${isGerman ? 'Freundlich, persönlich, motivierend' : 'Friendly, personal, motivating'}
- ${isGerman ? '1 Emoji passend zur Stimmung' : '1 emoji matching the mood'}
- ${isGerman ? 'Actionable - gib konkrete nächste Schritte' : 'Actionable - give concrete next steps'}
- ${languageInstruction}
- ${isGerman ? 'Kurz und prägnant' : 'Short and concise'}

${isGerman ? 'RÜCKGABE als JSON' : 'RETURN as JSON'}:
{
  "message": "${isGerman ? 'Dein hilfreicher Tipp oder Antwort' : 'Your helpful tip or answer'}",
  "animation": "celebrate" | "question" | "shake" | "wave",
  "actionButton": { "label": "${isGerman ? 'Button-Text' : 'Button text'}", "action": "action-name" } // optional
}

Animation-Guide:
- "celebrate": ${isGerman ? 'Bei Erfolgen, guten Nachrichten, Glückwünschen' : 'For successes, good news, congratulations'}
- "question": ${isGerman ? 'Bei Fragen, Tipps, Vorschlägen' : 'For questions, tips, suggestions'}
- "shake": ${isGerman ? 'Bei Problemen, Warnungen' : 'For problems, warnings'}
- "wave": ${isGerman ? 'Bei Begrüßungen, neutralen Infos' : 'For greetings, neutral info'}`;

    try {
      const response = await client.models.generateContent({
        model: 'gemini-3-flash-preview', // Use Flash 3.0 for Echobot
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 1.0, // Gemini 3 recommends default 1.0
          maxOutputTokens: 200,
          responseMimeType: 'application/json'
        }
      });

      let text = response.text;
      
      // Clean markdown code blocks if present
      text = text.trim();
      if (text.startsWith('```')) {
        text = text.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      }

      try {
        const parsed = JSON.parse(text);
        return {
          message: parsed.message || fallbackMessage,
          animation: parsed.animation || 'wave',
          actionButton: parsed.actionButton
        };
      } catch (parseError) {
        // If JSON parsing fails, use text directly
        console.warn('Echobot returned plain text:', text);
        return {
          message: text,
          animation: 'wave'
        };
      }
    } catch (error) {
      console.error('Echobot response generation failed:', error);
      // Fallback
      return {
        message: fallbackMessage,
        animation: 'wave'
      };
    }
  },

  /**
   * Chat with Echobot - Full conversational mode
   * Powered by Gemini 2.5 Pro with conversation history
   * Now supports language parameter for system language and user data context
   */
  async chatWithEchobot(
    userMessage: string,
    chatHistory: Array<{ role: 'user' | 'echobot'; content: string }>,
    context: {
      page: string;
      userRole: string;
      organizationStats: any;
      language?: SupportedLanguage; // Optional language override
      userData?: {
        tasks?: Array<{
          id: string;
          title: string;
          description?: string;
          dueDate: Date | string;
          completed: boolean;
          priority: 'low' | 'medium' | 'high';
          type: string;
          leadName?: string;
        }>;
        opportunities?: Array<{
          id: string;
          title: string;
          stage: string;
          value?: number;
          companyName?: string;
          probability?: number;
          expectedCloseDate?: Date | string;
        }>;
        leads?: Array<{
          id: string;
          firstName: string;
          lastName: string;
          company: string;
          stage: string;
          score?: number;
        }>;
      };
    }
  ): Promise<{
    message: string;
    animation: 'celebrate' | 'question' | 'shake' | 'wave';
    actionButtons?: Array<{ label: string; action: string }>;
    taskToCreate?: {
      title: string;
      description?: string;
      dueDate: string;
      priority: 'low' | 'medium' | 'high';
      type: 'call' | 'email' | 'meeting' | 'follow_up' | 'other';
    };
  }> {
    // Try to use Orchestrator Agent first (A2A Architecture)
    const orchestrator = AgentRegistry.getInstance().getAgent('orchestrator-agent');
    if (orchestrator) {
      console.log('🤖 Delegating chat to Orchestrator Agent...');
      try {
        const response = await orchestrator.onMessage({
          id: `msg-${Date.now()}`,
          sender: 'user',
          recipient: 'orchestrator-agent',
          intent: 'chat',
          content: userMessage,
          timestamp: Date.now()
        });

        if (response.status === 'success') {
          // Parse the response if it looks like JSON, otherwise wrap it
          let result = response.content;
          if (typeof result === 'string') {
             try {
               // Try to parse if it's JSON string
               if (result.trim().startsWith('{')) {
                 const parsed = JSON.parse(result);
                 // Validate it has message
                 if (parsed.message) {
                   return {
                     message: parsed.message,
                     animation: parsed.animation || 'wave',
                     actionButtons: parsed.actionButtons,
                     taskToCreate: parsed.taskToCreate
                   };
                 }
               }
             } catch (e) {
               // Not JSON, continue to wrap
             }
             
             // Wrap plain text response
             return {
               message: result,
               animation: 'wave'
             };
          } else {
            // It's already an object (if Orchestrator returned object)
            return {
               message: result.message || JSON.stringify(result),
               animation: result.animation || 'wave',
               actionButtons: result.actionButtons,
               taskToCreate: result.taskToCreate
            };
          }
        }
      } catch (error) {
        console.error('Orchestrator failed, falling back to legacy Gemini chat:', error);
      }
    }

    const client = getClient();
    
    // Get language configuration - use provided language or system default
    const langConfig = context.language 
      ? getLanguageConfig(context.language) 
      : getAILanguage();
    const isGerman = langConfig.code === 'de';

    // Log context for debugging
    console.log('🤖 Echobot Chat Context:', {
      page: context.page,
      userRole: context.userRole,
      hasUserData: !!context.userData,
      taskCount: context.userData?.tasks?.length || 0,
      opportunityCount: context.userData?.opportunities?.length || 0,
      leadCount: context.userData?.leads?.length || 0
    });

    // Build user data context string
    let userDataContext = '';
    if (context.userData) {
      const { tasks, opportunities, leads } = context.userData;
      
      // Include ALL tasks (both open and completed) for full visibility
      if (tasks && tasks.length > 0) {
        const openTasks = tasks.filter(t => !t.completed);
        const completedTasks = tasks.filter(t => t.completed);
        const overdueTasks = openTasks.filter(t => new Date(t.dueDate) < new Date());
        
        userDataContext += isGerman 
          ? `\n\n=== TASKS DES NUTZERS ===
Gesamt: ${tasks.length} Tasks (${openTasks.length} offen, ${completedTasks.length} erledigt, ${overdueTasks.length} überfällig)

Offene Tasks:
${openTasks.length > 0 ? openTasks.slice(0, 15).map(t => `• "${t.title}" - Priorität: ${t.priority}, Fällig: ${new Date(t.dueDate).toLocaleDateString('de-DE')}, Typ: ${t.type}${t.leadName && t.leadName !== 'undefined undefined' ? `, Kontakt: ${t.leadName}` : ''}`).join('\n') : '(Keine offenen Tasks)'}`
          : `\n\n=== USER'S TASKS ===
Total: ${tasks.length} tasks (${openTasks.length} open, ${completedTasks.length} completed, ${overdueTasks.length} overdue)

Open Tasks:
${openTasks.length > 0 ? openTasks.slice(0, 15).map(t => `• "${t.title}" - Priority: ${t.priority}, Due: ${new Date(t.dueDate).toLocaleDateString('en-US')}, Type: ${t.type}${t.leadName && t.leadName !== 'undefined undefined' ? `, Contact: ${t.leadName}` : ''}`).join('\n') : '(No open tasks)'}`;
      } else {
        userDataContext += isGerman 
          ? '\n\n=== TASKS DES NUTZERS ===\nKeine Tasks vorhanden.'
          : '\n\n=== USER\'S TASKS ===\nNo tasks available.';
      }
      
      if (opportunities && opportunities.length > 0) {
        const activeOpps = opportunities.filter(o => o.stage !== 'won' && o.stage !== 'lost');
        const wonOpps = opportunities.filter(o => o.stage === 'won');
        const totalValue = activeOpps.reduce((sum, o) => sum + (o.value || 0), 0);
        
        userDataContext += isGerman 
          ? `\n\n=== OPPORTUNITIES DES NUTZERS ===
Gesamt: ${opportunities.length} Opportunities (${activeOpps.length} aktiv, ${wonOpps.length} gewonnen)
Pipeline-Wert: €${totalValue.toLocaleString('de-DE')}

Aktive Opportunities:
${activeOpps.length > 0 ? activeOpps.slice(0, 15).map(o => `• "${o.title}" - Stage: ${o.stage}, Wert: €${(o.value || 0).toLocaleString('de-DE')}, Firma: ${o.companyName || 'Keine'}`).join('\n') : '(Keine aktiven Opportunities)'}`
          : `\n\n=== USER'S OPPORTUNITIES ===
Total: ${opportunities.length} opportunities (${activeOpps.length} active, ${wonOpps.length} won)
Pipeline Value: €${totalValue.toLocaleString('en-US')}

Active Opportunities:
${activeOpps.length > 0 ? activeOpps.slice(0, 15).map(o => `• "${o.title}" - Stage: ${o.stage}, Value: €${(o.value || 0).toLocaleString('en-US')}, Company: ${o.companyName || 'None'}`).join('\n') : '(No active opportunities)'}`;
      } else {
        userDataContext += isGerman 
          ? '\n\n=== OPPORTUNITIES DES NUTZERS ===\nKeine Opportunities vorhanden.'
          : '\n\n=== USER\'S OPPORTUNITIES ===\nNo opportunities available.';
      }
      
      if (leads && leads.length > 0) {
        userDataContext += isGerman 
          ? `\n\n=== KONTAKTE DES NUTZERS ===
Gesamt: ${leads.length} Kontakte

Kontaktliste:
${leads.slice(0, 15).map(l => `• ${l.firstName} ${l.lastName} - Firma: ${l.company}, Stage: ${l.stage}${l.score ? `, Score: ${l.score}` : ''}`).join('\n')}`
          : `\n\n=== USER'S CONTACTS ===
Total: ${leads.length} contacts

Contact List:
${leads.slice(0, 15).map(l => `• ${l.firstName} ${l.lastName} - Company: ${l.company}, Stage: ${l.stage}${l.score ? `, Score: ${l.score}` : ''}`).join('\n')}`;
      } else {
        userDataContext += isGerman 
          ? '\n\n=== KONTAKTE DES NUTZERS ===\nKeine Kontakte vorhanden.'
          : '\n\n=== USER\'S CONTACTS ===\nNo contacts available.';
      }
    } else {
      userDataContext = isGerman 
        ? '\n\n(Keine Nutzerdaten verfügbar - bitte Seite aktualisieren)'
        : '\n\n(No user data available - please refresh page)';
    }
    
    console.log('📊 User Data Context Length:', userDataContext.length, 'chars');

    const systemPrompt = isGerman 
      ? `Du bist Echobot, der CRM-Assistent. Du HAST ZUGRIFF auf die Nutzerdaten - sie sind HIER in diesem Prompt!

**NUTZERDATEN (DU HAST ZUGRIFF!):**
${userDataContext}

**KONTEXT:**
- Seite: ${context.page} | Rolle: ${context.userRole}
- Org: ${context.organizationStats.leadCount || 0} Leads, ${context.organizationStats.opportunityCount || 0} Opportunities

**KRITISCHE ANWEISUNG:**
Du MUSST die obigen Daten nutzen! Sage NIEMALS "Ich habe keinen Zugriff" - die Daten stehen OBEN!
- Frage "Wie viele Tasks?" → Zähle die Tasks OBEN und antworte mit der Zahl!
- Frage "Welche Opportunities?" → Liste die Opportunities von OBEN!
- Frage nach Kontakten → Nenne Namen aus der Liste OBEN!

**STIL:** Kurz, freundlich, deutsch, mit Emojis. Nenne konkrete Namen/Zahlen aus den Daten!

**TASK ERSTELLUNG:**
Wenn der Nutzer einen Task erstellen möchte (z.B. "Erstelle einen Task", "Leg einen Task an", "Erinnere mich an..."), füge taskToCreate hinzu:
- Erkenne Titel aus der Anfrage
- Setze sinnvolles Datum (heute = ${new Date().toISOString().split('T')[0]}, morgen, nächste Woche, etc.)
- Wähle passenden Typ: "call", "email", "meeting", "follow_up", "other"
- Wähle Priorität basierend auf Dringlichkeit: "low", "medium", "high"

**AKTIONEN (actionButtons):**
Wenn du eine Navigation oder Modal-Öffnung empfiehlst:
- "Kampagne erstellen" → action: "navigate-to-google-ads"
- "Lead anlegen" → action: "open-add-lead-modal"  
- "Tasks ansehen" → action: "navigate-to-tasks"
- "Opportunities ansehen" → action: "navigate-to-opportunities"

**ANTWORT NUR ALS JSON:**
{
  "message": "Deine Antwort hier",
  "animation": "wave" | "celebrate" | "question" | "shake",
  "actionButtons": [{"label": "Button Text", "action": "action-name"}],
  "taskToCreate": {
    "title": "Task-Titel",
    "description": "Optionale Beschreibung",
    "dueDate": "YYYY-MM-DD",
    "priority": "low" | "medium" | "high",
    "type": "call" | "email" | "meeting" | "follow_up" | "other"
  }
}
Hinweis: actionButtons und taskToCreate sind optional - nur hinzufügen wenn relevant!`
      : `You are Echobot, the CRM assistant. You HAVE ACCESS to user data - it's HERE in this prompt!

**USER DATA (YOU HAVE ACCESS!):**
${userDataContext}

**CONTEXT:**
- Page: ${context.page} | Role: ${context.userRole}
- Org: ${context.organizationStats.leadCount || 0} Leads, ${context.organizationStats.opportunityCount || 0} Opportunities

**CRITICAL INSTRUCTION:**
You MUST use the data above! NEVER say "I don't have access" - the data is ABOVE!
- Question "How many tasks?" → Count tasks ABOVE and answer with the number!
- Question "Which opportunities?" → List opportunities from ABOVE!
- Question about contacts → Name contacts from the list ABOVE!

**STYLE:** Short, friendly, English, with emojis. Mention concrete names/numbers from the data!

**TASK CREATION:**
If the user wants to create a task (e.g. "Create a task", "Add a task", "Remind me to..."), include taskToCreate:
- Extract title from the request
- Set sensible date (today = ${new Date().toISOString().split('T')[0]}, tomorrow, next week, etc.)
- Choose appropriate type: "call", "email", "meeting", "follow_up", "other"
- Choose priority based on urgency: "low", "medium", "high"

**ACTIONS (actionButtons):**
For navigation or modal opening suggestions:
- "Create campaign" → action: "navigate-to-google-ads"
- "Add lead" → action: "open-add-lead-modal"  
- "View tasks" → action: "navigate-to-tasks"
- "View opportunities" → action: "navigate-to-opportunities"

**RESPOND ONLY AS JSON:**
{
  "message": "Your answer here",
  "animation": "wave" | "celebrate" | "question" | "shake",
  "actionButtons": [{"label": "Button Text", "action": "action-name"}],
  "taskToCreate": {
    "title": "Task title",
    "description": "Optional description",
    "dueDate": "YYYY-MM-DD",
    "priority": "low" | "medium" | "high",
    "type": "call" | "email" | "meeting" | "follow_up" | "other"
  }
}
Note: actionButtons and taskToCreate are optional - only include when relevant!`;

    // Build conversation history - ensure valid roles (user/model only)
    const conversationHistory = chatHistory.map((msg) => ({
      role: msg.role === 'echobot' ? 'model' : 'user',
      parts: [{ text: msg.content }]
    }));
    
    // Fallback messages by language
    const fallbackMessages = {
      de: {
        error: 'Entschuldigung, ich konnte das nicht verstehen. Kannst du die Frage anders formulieren?',
        technicalError: 'Entschuldigung, da gab es ein technisches Problem. Versuch es bitte nochmal! 🤖'
      },
      en: {
        error: 'Sorry, I couldn\'t understand that. Could you rephrase the question?',
        technicalError: 'Sorry, there was a technical problem. Please try again! 🤖'
      }
    };

    // Build enriched user message with data context for data-related questions or task creation
    const dataKeywords = ['task', 'aufgabe', 'opportunity', 'opportunities', 'kontakt', 'contact', 'lead', 'wie viele', 'how many', 'zeig', 'show', 'liste', 'list', 'meine', 'my', 'erstelle', 'create', 'anlegen', 'hinzufügen', 'add', 'erinnere', 'remind', 'todo', 'to-do'];
    const isDataQuestion = dataKeywords.some(kw => userMessage.toLowerCase().includes(kw));
    
    let enrichedUserMessage = userMessage;
    if (isDataQuestion && userDataContext) {
      enrichedUserMessage = isGerman
        ? `KONTEXT - MEINE DATEN (nutze diese für deine Antwort!):
${userDataContext}

MEINE FRAGE: ${userMessage}

Beantworte die Frage basierend auf den obigen Daten. Sage NICHT "ich habe keinen Zugriff" - die Daten stehen oben!`
        : `CONTEXT - MY DATA (use this for your answer!):
${userDataContext}

MY QUESTION: ${userMessage}

Answer based on the data above. Do NOT say "I don't have access" - the data is above!`;
    }
    
    console.log('📝 Enriched message:', isDataQuestion ? 'YES (data question)' : 'NO', 'Length:', enrichedUserMessage.length);

    try {
      const response = await client.models.generateContent({
        model: 'gemini-3-flash-preview', // Use Flash 3.0 for Echobot chat
        contents: [
          ...conversationHistory,
          { role: 'user', parts: [{ text: enrichedUserMessage }] }
        ],
        config: {
          systemInstruction: systemPrompt,
          temperature: 1.0, // Gemini 3 recommends default 1.0
          maxOutputTokens: 500,
          responseMimeType: 'application/json'
        }
      });

      let text = response.text;
      
      // Clean markdown code blocks if present
      text = text.trim();
      if (text.startsWith('```')) {
        text = text.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      }
      
      // Try to parse as JSON
      try {
        const parsed = JSON.parse(text);
        return {
          message: parsed.message || fallbackMessages[langConfig.code].error,
          animation: parsed.animation || 'question',
          actionButtons: parsed.actionButtons,
          taskToCreate: parsed.taskToCreate
        };
      } catch (parseError) {
        // If JSON parsing fails, the model returned plain text - use it directly
        console.warn('Echobot returned plain text instead of JSON:', text);
        return {
          message: text,
          animation: 'wave'
        };
      }
    } catch (error) {
      console.error('Echobot chat failed:', error);
      // Fallback response in correct language
      return {
        message: fallbackMessages[langConfig.code].technicalError,
        animation: 'shake'
      };
    }
  },

  /**
   * Generate personalized meeting tips based on lead, company and context
   */
  async generateMeetingTips(context: {
    leadName: string;
    leadStage: string;
    leadScore: number;
    companyName?: string;
    industry?: string;
    companyDescription?: string;
    notes?: string[];
    products?: string[];
    opportunities?: { title: string; stage: string; value: number }[];
  }): Promise<{
    conversationOpeners: string[];
    objectionHandling: { objection: string; response: string }[];
    productRecommendations: string[];
    closingTips: string[];
    keyInsights: string[];
  }> {
    try {
      const client = getClient();
      
      const prompt = `Du bist ein erfahrener Sales-Coach. Generiere personalisierte Meeting-Tipps für ein Verkaufsgespräch.

KONTEXT:
- Kontakt: ${context.leadName}
- Stage: ${context.leadStage}
- Score: ${context.leadScore}%
${context.companyName ? `- Unternehmen: ${context.companyName}` : ''}
${context.industry ? `- Branche: ${context.industry}` : ''}
${context.companyDescription ? `- Über das Unternehmen: ${context.companyDescription}` : ''}
${context.notes?.length ? `- Bisherige Notizen: ${context.notes.slice(-3).join('; ')}` : ''}
${context.products?.length ? `- Interessierte Produkte: ${context.products.join(', ')}` : ''}
${context.opportunities?.length ? `- Aktive Opportunities: ${context.opportunities.map(o => `${o.title} (${o.stage}, €${o.value})`).join('; ')}` : ''}

Generiere:
1. 2-3 konkrete Gesprächseinstiege passend zur Stage und Branche
2. 2-3 typische Einwände für diese Branche mit konkreten Antworten
3. 2-3 Produktempfehlungen/Themen die passen könnten
4. 2-3 Abschlusstechniken passend zur Stage
5. 2-3 Key Insights die man beachten sollte

Antworte auf Deutsch, praxisnah und konkret. Keine allgemeinen Floskeln.

ANTWORT NUR ALS JSON:
{
  "conversationOpeners": ["...", "..."],
  "objectionHandling": [{"objection": "...", "response": "..."}, ...],
  "productRecommendations": ["...", "..."],
  "closingTips": ["...", "..."],
  "keyInsights": ["...", "..."]
}`;

      const response = await client.models.generateContent({
        model: 'gemini-3-flash-preview', // Use Flash 3.0 for tips
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        config: {
          temperature: 1.0,
          maxOutputTokens: 1000,
          responseMimeType: 'application/json'
        }
      });

      // Unified SDK: response.text is a property
      let text = response.text || '';
      
      // Clean markdown code blocks if present
      text = text.trim();
      if (text.startsWith('```')) {
        text = text.replace(/^```json\s*/gi, '').replace(/^```\s*/gi, '').replace(/```\s*$/gi, '').trim();
      }

      const parsed = JSON.parse(text);
      return {
        conversationOpeners: parsed.conversationOpeners || [],
        objectionHandling: parsed.objectionHandling || [],
        productRecommendations: parsed.productRecommendations || [],
        closingTips: parsed.closingTips || [],
        keyInsights: parsed.keyInsights || []
      };
    } catch (error) {
      console.error('Generate meeting tips failed:', error);
      // Return empty arrays on error
      return {
        conversationOpeners: [],
        objectionHandling: [],
        productRecommendations: [],
        closingTips: [],
        keyInsights: []
      };
    }
  },

  /**
   * Specialized generation for Agents that supports Function Calling (Tools)
   * Returns the raw response object to allow handling function calls
   */
  async generateAgentResponse(
    prompt: string,
    options?: {
      temperature?: number;
      maxOutputTokens?: number;
      tools?: any[];
      model?: string;
      thinkingLevel?: 'low' | 'medium' | 'high' | 'minimal';
      history?: any[]; // Allow passing chat history
    }
  ): Promise<any> {
    try {
      const client = getClient();
      const modelName = options?.model || 'gemini-3-flash-preview';
      
      const generationConfig: any = {
        temperature: options?.temperature ?? 1.0,
        maxOutputTokens: options?.maxOutputTokens ?? 4096
      };

      if (options?.thinkingLevel) {
        generationConfig.thinkingConfig = {
          thinkingLevel: options.thinkingLevel
        };
      }

      // Create a chat session to manage history/multi-turn
      // Note: tools must be inside config, not as separate parameter
      const chat = client.chats.create({ 
        model: modelName,
        config: {
          ...generationConfig,
          tools: options?.tools
        },
        history: options?.history || []
      });

      const response = await chat.sendMessage({ message: prompt });

      return {
        text: response.text || null,
        functionCalls: response.functionCalls || [],
        rawResult: response, // Return the full result object for history tracking if needed
        chatSession: chat  // Return session for multi-turn
      };
    } catch (error) {
      console.error('Gemini Agent generation failed:', error);
      throw error;
    }
  },

  /**
   * Generic generation method for flexible use
   * Supports grounding (web search) and JSON mode
   * 
   * Uses Unified Google GenAI SDK: client.models.generateContent()
   */
  async generateWithGemini(
    prompt: string,
    options?: {
      temperature?: number;
      maxOutputTokens?: number;
      jsonMode?: boolean; // Deprecated: use responseMimeType in config instead
      tools?: any[];
      model?: string; // Support model selection
      thinkingLevel?: 'low' | 'medium' | 'high' | 'minimal'; // Support thinking level
    }
  ): Promise<string> {
    try {
      const client = getClient();
      const modelName = options?.model || 'gemini-3-flash-preview';
      
      const config: any = {
        temperature: options?.temperature ?? 1.0, // Gemini 3 recommends default 1.0
        maxOutputTokens: options?.maxOutputTokens ?? 1000
      };

      // Auto-detect JSON requirement in prompt or options
      if (options?.jsonMode || prompt.includes('JSON')) {
        config.responseMimeType = 'application/json';
      }
      
      // Support Thinking Level (Gemini 3)
      if (options?.thinkingLevel) {
        config.thinkingConfig = {
          thinkingLevel: options.thinkingLevel
        };
      }

      // Unified SDK: client.models.generateContent()
      const response = await client.models.generateContent({
        model: modelName,
        contents: [{ parts: [{ text: prompt }] }],
        config,
        tools: options?.tools
      });

      // Unified SDK: response.text is a property
      return response.text || '';
    } catch (error) {
      console.error('Gemini generic generation failed:', error);
      throw error;
    }
  },

  /**
   * Simple chat function for quick AI responses
   * Used by Echobot for simple queries
   */
  async chat(
    prompt: string,
    thinkingLevel: 'low' | 'medium' | 'high' = 'low'
  ): Promise<string> {
    try {
      const client = getClient();
      const modelName = 'gemini-3-flash-preview';
      
      const response = await client.models.generateContent({
        model: modelName,
        contents: [{ parts: [{ text: prompt }] }],
        config: {
          temperature: 0.7,
          maxOutputTokens: 1024,
          thinkingConfig: {
            thinkingLevel: thinkingLevel
          }
        }
      });

      return response.text || '';
    } catch (error) {
      console.error('Gemini chat failed:', error);
      throw error;
    }
  }
};
